// @flow
import type {Element} from 'react'
import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react'

import classNames from 'classnames'
import {CKEditor as CKEditorComponent} from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import Mention from '@ckeditor/ckeditor5-mention/src/mention'
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials'
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat'
import Markdown from '@ckeditor/ckeditor5-markdown-gfm/src/markdown'
import Heading from '@ckeditor/ckeditor5-heading/src/heading'
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph'
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold'
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic'
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough'
import Code from '@ckeditor/ckeditor5-basic-styles/src/code'
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote'
import Link from '@ckeditor/ckeditor5-link/src/link'
import AutoLink from '@ckeditor/ckeditor5-link/src/autolink'
import List from '@ckeditor/ckeditor5-list/src/list'
import plainTextToHtml from '@ckeditor/ckeditor5-clipboard/src/utils/plaintexttohtml'

import AppRouter from 'utils/AppRouter'
import getCurrentLocale from 'utils/getCurrentLocale'

import styles from './index.scss'

export type FakeOnChangeEvent = $ReadOnly<{
  target: $ReadOnly<{
    name: string,
    value: string,
  }>,
}>

export type FakeOnBlurEvent = $ReadOnly<{
  target: $ReadOnly<{
    name: string,
  }>,
}>

export type FakeOnFocusEvent = FakeOnBlurEvent

export type FakeOnKeyDownEvent = $ReadOnly<{
  key: string,
  shiftKey: boolean,
  preventDefault: () => void,
}>

export type CKAutocompleteItem = $ReadOnly<{
  link: string,
  name: string,
  id: string,
}>

type Props = $ReadOnly<{
  mode: 'classic' | 'inline',
  buttonSet?: 'font-styles-only' | 'basic' | 'full',
  value: string,
  onChange?: FakeOnChangeEvent => void,
  onBlur?: FakeOnBlurEvent => void,
  onFocus?: FakeOnFocusEvent => void,
  onKeyDown?: FakeOnKeyDownEvent => void,
  onReady: (any) => void,
  name: string,
  getFeed: (string) => $ReadOnlyArray<CKAutocompleteItem>,
  disabled: boolean,
  useCustomCopyHandler: boolean,
  placeholder?: string,
}>

function mentionCustomization(editor) {
  editor.conversion.for('upcast').elementToAttribute({
    view: {
      name: 'a',
      attributes: {
        href: /^(?:\/organizations\b)(?:\/[\w]+)(?:\/orgnav\b)(?:\/roles\b)(?:\/[\w]+)$/,
      },
    },
    model: {
      key: 'mention',
      value: (viewItem) => (
        editor.plugins.get('Mention').toMentionAttribute(viewItem, {
          link: viewItem.getAttribute('href'),
        })
      ),
    },
    converterPriority: 'high',
  })

  editor.conversion.for('downcast').attributeToElement({
    model: 'mention',
    view: (modelAttributeValue, {writer}) => {
      if (!modelAttributeValue)
        return null

      return writer.createAttributeElement('a', {
        class: 'mention',
        'data-mention': modelAttributeValue.id,
        href: modelAttributeValue.link,
      }, {
        priority: 20,
        id: modelAttributeValue.uid,
      })
    },
    converterPriority: 'high',
  })
}

function mentionItemRenderer(item: CKAutocompleteItem) {
  const buttonItem = document.createElement('button')

  buttonItem.classList.add('ck', 'ck-button', 'ck-off', 'ck-button_with-text')
  buttonItem.setAttribute('type', 'button')
  buttonItem.setAttribute('tabIndex', '-1')

  const itemElement = document.createElement('span')

  itemElement.classList.add('ck', 'ck-button__label')
  itemElement.textContent = `${item.name}`

  buttonItem.appendChild(itemElement)

  return buttonItem
}

const locale = getCurrentLocale(true).toLowerCase()

const requiredPlugins = [
  Essentials,
  Markdown,
  Mention,
  mentionCustomization,
  Autoformat,
  Heading,
]
const fontStylePlugins = [
  Bold,
  Italic,
  Strikethrough,
]

const basicPlugins = [
  ...fontStylePlugins,
  Code,
  Link,
  AutoLink,
]

const fontStylesOnlyToolbar = [
  'bold', 'italic', 'strikethrough', '|',
  'undo', 'redo',
]

const basicToolbar = [
  'bold', 'italic', 'strikethrough', '|',
  'code', 'link', '|',
  'undo', 'redo',
]

const fullToolbar = [
  'heading', '|',
  'bold', 'italic', 'strikethrough', '|',
  'code', 'blockQuote', 'link', '|',
  'bulletedList', 'numberedList', '|',
  'undo', 'redo',
]

const fullPlugins = [
  ...basicPlugins,
  Paragraph, BlockQuote, List,
]

const getPlugins = (buttonSetPlugins) => ([
  ...requiredPlugins,
  ...buttonSetPlugins,
])

const MULTIPLE_NEWLINE_REGEX = /(\n|\r|\r\n){2,}/g

function isModifiedEvent(event: MouseEvent | KeyboardEvent) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
}

const getLinkAttributes = (event: MouseEvent | KeyboardEvent) => {
  // `eventTarget` is really a `HTMLElement`, but not `EventTarget` as Flow think,
  //  at least in real cases, so let's ignore Flow for now
  // $FlowFixMe[incompatible-type]
  const eventTarget: HTMLElement = event.target
  // $FlowFixMe[incompatible-type]
  const eventCurrentTarget: HTMLElement = event.currentTarget

  // $FlowFixMe[method-unbinding]
  const anchorElement = eventTarget.closest && eventTarget.closest('a')

  if (!anchorElement)
    return null

  const target = anchorElement.getAttribute('target')

  const href = anchorElement.getAttribute('href')

  if (!href)
    return null

  const isEditorEnabled = eventCurrentTarget.getAttribute('data-ck-editor-disabled') === 'false'

  if (isEditorEnabled)
    return null

  const isExternal = target === '_blank'

  return {
    href,
    isExternal,
  }
}

function sanitizeClipboardOutput(content: string): string {
  return content.replace(MULTIPLE_NEWLINE_REGEX, '$1').trim()
}

const openLinkInNewTab = (href: string) => {
  Object.assign(document.createElement('a'), {
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
  }).click()
}

const openLinkInTheSameTab = (event: MouseEvent | KeyboardEvent, href: string) => {
  try {
    const url = new URL(href)
    AppRouter.instance.redirectTo(url.pathname)
  } catch {
    AppRouter.instance.redirectTo(href)
  }
}

function CKEditor({
  mode,
  buttonSet,
  value,
  onChange: rawOnChange,
  onBlur: rawOnBlur,
  onReady: rawOnReady,
  onFocus: rawOnFocus,
  onKeyDown,
  name,
  getFeed,
  disabled,
  useCustomCopyHandler,
  placeholder,
}: Props): Element<"div"> {
  const [editor, setEditor] = useState(null)

  const onKeyDownRef = useRef(onKeyDown)
  const wrapperRef = useRef<?HTMLDivElement>(null)

  useEffect(() => {
    const wrapper = wrapperRef.current

    const onClickHandler = (event: MouseEvent) => {
      if (isModifiedEvent(event))
        return

      const linkAttributes = getLinkAttributes(event)

      if (!linkAttributes || linkAttributes.isExternal)
        return

      event.preventDefault()
      event.stopPropagation()

      openLinkInTheSameTab(event, linkAttributes.href)
    }

    const onKeyDownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Enter')
        return

      const linkAttributes = getLinkAttributes(event)

      if (!linkAttributes)
        return

      event.preventDefault()
      event.stopPropagation()

      if (linkAttributes.isExternal || isModifiedEvent(event))
        openLinkInNewTab(linkAttributes.href)
      else
        openLinkInTheSameTab(event, linkAttributes.href)
    }

    const onCopyHandler = (event: ClipboardEvent) => {
      if (!useCustomCopyHandler)
        return

      const selectionContent = document.getSelection()?.toString()

      if (!selectionContent || !event.clipboardData)
        return

      event.stopPropagation()
      const sanitizedText = sanitizeClipboardOutput(selectionContent)
      event.clipboardData?.setData('text/plain', sanitizedText)
      event.clipboardData?.setData('text/html', plainTextToHtml(sanitizedText))
    }

    wrapper?.addEventListener('click', onClickHandler)
    wrapper?.addEventListener('keydown', onKeyDownHandler)
    wrapper?.addEventListener('copy', onCopyHandler)
    wrapper?.addEventListener('cut', onCopyHandler)

    return () => {
      wrapper?.removeEventListener('click', onClickHandler)
      wrapper?.removeEventListener('keydown', onKeyDownHandler)
      wrapper?.removeEventListener('copy', onCopyHandler)
      wrapper?.removeEventListener('cut', onCopyHandler)
    }
  }, [useCustomCopyHandler])

  useEffect(() => {
    editor?.editing.view.document.on('enter', (event, data) => {
      if (!onKeyDownRef.current)
        return

      onKeyDownRef.current({
        key: 'Enter',
        shiftKey: data.domEvent.shiftKey,
        preventDefault: () => {
          event.stop()
          data.preventDefault()
        },
      })
    })

    editor?.editing.view.document.on('keydown', (event, data) => {
      if (!onKeyDownRef.current)
        return

      onKeyDownRef.current({
        key: data.domEvent.key,
        shiftKey: data.domEvent.shiftKey,
        preventDefault: () => {
          event.stop()
          data.preventDefault()
        },
      })
    })
  }, [editor, onKeyDownRef])

  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  }, [onKeyDown])

  const toolbarMapping = useMemo(() => ({
    'font-styles-only': fontStylesOnlyToolbar,
    basic: basicToolbar,
    full: fullToolbar,
  }), [])

  const pluginsMapping = useMemo(() => ({
    'font-styles-only': fontStylePlugins,
    basic: basicPlugins,
    full: fullPlugins,
  }), [])

  const resolvedButtonSet = buttonSet || (mode === 'classic' ? 'full' : 'basic')

  const config = useMemo(() => {
    const plugins = getPlugins(pluginsMapping[resolvedButtonSet])

    return {
      plugins,
      language: locale,
      toolbar: toolbarMapping[resolvedButtonSet] || basicToolbar,
      link: {
        defaultProtocol: 'https://',
        decorators: {
          isExternal: {
            mode: 'automatic',
            callback: (href: string) => {
              try {
                const url = new URL(href)
                return url.hostname !== window.location.hostname
              } catch {
                return false
              }
            },
            attributes: {
              target: '_blank',
              rel: 'noopener noreferrer',
            },
          },
        },
      },
      heading: {
        options: [
          {
            model: 'paragraph',
            title: I18n.t('react.components.ckeditor.paragraph'),
            class: 'ck-heading_paragraph',
          },
          {
            model: 'heading1',
            view: 'h3',
            title: I18n.t('react.components.ckeditor.heading'),
            class: 'ck-heading_heading3',
          },
        ],
      },
      mention: {
        feeds: [
          {
            marker: '@',
            feed: getFeed,
            minimumCharacters: 0,
            itemRenderer: mentionItemRenderer,
          },
        ],
      },
      floatSpacePreferRight: true,
      placeholder,
    }
  }, [getFeed, placeholder, resolvedButtonSet, toolbarMapping, pluginsMapping])

  const onChange = useCallback(() => {
    if (!rawOnChange)
      return

    const data = editor?.getData() || ''
    rawOnChange({
      target: {
        name,
        value: data,
      },
    })
  }, [editor, rawOnChange, name])

  const onBlur = useCallback(() => {
    if (!rawOnBlur)
      return

    rawOnBlur({
      target: {
        name,
      },
    })
  }, [rawOnBlur, name])

  const onFocus = useCallback(() => {
    if (!rawOnFocus)
      return

    rawOnFocus({
      target: {
        name,
      },
    })
  }, [rawOnFocus, name])

  const onReady = useCallback((createdEditor) => {
    rawOnReady(createdEditor)
    setEditor(createdEditor)
  }, [rawOnReady, setEditor])

  const containerClassName = classNames(styles.container, {
    [styles.containerDisabled]: disabled,
  })

  return (
    <div ref={wrapperRef} className={containerClassName} data-ck-editor-disabled={disabled}>
      <CKEditorComponent
        config={config}
        editor={ClassicEditor}
        data={value}
        onReady={onReady}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  )
}

CKEditor.defaultProps = {
  onChange: () => {},
  onBlur: () => {},
  onFocus: () => {},
  name: '',
  getFeed: (): Array<any> => ([]),
  onReady: () => {},
  disabled: false,
  useCustomCopyHandler: false,
  placeholder: '',
}

export default CKEditor
